import { Box, Button } from '@mui/material'
import MessageIcon from '@mui/icons-material/Message'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PatientRegistrationComponent from '../campManagement/component/PatientRegistration'
import { useLocation } from 'react-router-dom'
import PatientSearchDialog from './PatientSearchDialog'
import styles from './registration.module.scss'
import { useEffect, useState } from 'react'
import { resendNotification } from '../../services/CampManagementService'
import {
  // booleanType,
  emailType,
  FileDataTypes,
  phoneType,
  WhatsappText,
} from './Constants'
import { toast } from 'react-toastify'

interface RegistrationProps {
  patient?: any
  onSuccess?: () => void
  selectedCamp?: any
}

const Registration = ({
  patient,
  onSuccess,
  selectedCamp,
}: RegistrationProps) => {
  const location = useLocation()
  const campDetails = (location?.state as any)?.camp || selectedCamp
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<any>(null) // raw selection from dialog or incoming prop
  const [registrationPayload, setRegistrationPayload] = useState<any>(null) // normalized payload in required shape
  const [selectSeacrh, setSelectSeacrh] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  // console.log('incoming prop patient', JSON.stringify(patient))

  // Helper to calculate age from birthdate string
  const calculateAge = (birthDateStr: string) => {
    try {
      const bd = new Date(birthDateStr)
      if (isNaN(bd?.getTime())) return 0
      const diff = Date?.now() - bd?.getTime()
      const ageDt = new Date(diff)
      return Math?.abs(ageDt?.getUTCFullYear() - 1970)
    } catch {
      return 0
    }
  }

  // Transform selected patient data to a form-friendly structure
  const transformSelectedPatientData = (selectedData: any) => {
    if (!selectedData) return null

    // If data already looks like the expected simple object (from patient menu), return as-is
    if (selectedData?.firstName || selectedData?.phoneNumber) {
      return selectedData
    }

    // Build readable full name from different possible shapes
    let fullName = ''
    if (typeof selectedData?.name === FileDataTypes?.STRING) {
      fullName = selectedData?.name
    } else if (
      selectedData?.fhirData?.name &&
      Array.isArray(selectedData?.fhirData?.name)
    ) {
      const nameObj = selectedData?.fhirData?.name[0] || {}
      const given = Array.isArray(nameObj?.given)
        ? nameObj?.given?.join(' ')
        : nameObj?.given || ''
      fullName = `${given} ${nameObj?.family || ''}`.trim()
    } else if (selectedData?.fhirData?.text?.div) {
      // fallback: try to extract from FHIR text if necessary (not guaranteed)
      fullName = selectedData.fhirData.text.div.replace(/<[^>]+>/g, ' ').trim()
    }

    const nameParts = fullName ? fullName?.trim()?.split(/\s+/) : []
    const addressString =
      selectedData?.address ||
      (selectedData?.fhirData?.address && selectedData?.fhirData?.address[0]
        ? `${selectedData?.fhirData?.address[0]?.line?.join(', ') || ''} ${
            selectedData?.fhirData?.address[0]?.city || ''
          } ${selectedData?.fhirData?.address[0]?.postalCode || ''}`
        : '')
    const postalCodeMatch = addressString
      ? addressString.match(/\b\d{6}\b/)
      : null

    const telecom = selectedData.fhirData?.telecom || selectedData.telecom || []
    const phoneFromFhir = Array.isArray(telecom)
      ? telecom?.find((t: any) => t?.system === phoneType)?.value
      : undefined
    const emailFromFhir = Array.isArray(telecom)
      ? telecom?.find((t: any) => t?.system === emailType)?.value
      : undefined

    return {
      salutation:
        selectedData?.fhirData?.name?.[0]?.prefix?.[0] ||
        selectedData.salutation ||
        '',
      firstName: nameParts[0] || '',
      middleName:
        nameParts?.length > 2 ? nameParts?.slice(1, -1)?.join(' ') : '',
      lastName: nameParts?.length > 1 ? nameParts?.[nameParts?.length - 1] : '',
      age:
        selectedData?.age ||
        (selectedData?.birthDate
          ? calculateAge(selectedData?.birthDate)
          : selectedData?.fhirData?.birthDate
          ? calculateAge(selectedData?.fhirData?.birthDate)
          : 0),
      gender: selectedData?.gender || selectedData?.fhirData?.gender || '',
      dob: selectedData?.birthDate || selectedData?.fhirData?.birthDate || '',
      mobile:
        selectedData?.phoneNumber ||
        selectedData?.phoneNo ||
        phoneFromFhir ||
        '',
      emailId: emailFromFhir || selectedData?.emailId || '',
      town: postalCodeMatch
        ? addressString
            .replace(/\b\d{6}\b/, '')
            .trim()
            .replace(/,\s*$/, '')
        : addressString || '',
      postalCode: postalCodeMatch
        ? parseInt(postalCodeMatch[0])
        : selectedData?.postalCode ||
          (selectedData?.fhirData?.address?.[0]?.postalCode
            ? parseInt(selectedData?.fhirData?.address?.[0]?.postalCode)
            : ''),
      uhid:
        selectedData?.uhid ||
        (Array.isArray(selectedData?.fhirData?.identifier)
          ? (
              selectedData?.fhirData?.identifier?.find(
                (i: any) =>
                  (i.system || '').toLowerCase().includes('uhid') ||
                  (i.type &&
                    i.type.text &&
                    i.type.text.toLowerCase().includes('uhid'))
              ) || {}
            ).value
          : '') ||
        '',
      patientFhirId: selectedData?.id || selectedData?.fhirData?.id || '',
      existing: true,
      fhirData: selectedData?.fhirData || {},
    }
  }

  // Build the exact registration payload shape requested by you
  const buildRegistrationPayload = (selectedData: any) => {
    if (!selectedData) return null

    const t = transformSelectedPatientData(selectedData) || {}

    // prefer explicit uhid fields, fall back to identifiers inside fhirData
    let patientUhid = t?.uhid || ''

    // Get employeeId and volunteerId from the selected data or form data
    const employeeId = selectedData?.employeeId || ''
    const volunteerId = selectedData?.volunteerId || ''

    if (!patientUhid && Array.isArray(selectedData?.fhirData?.identifier)) {
      const uhidIdentifier = selectedData?.fhirData?.identifier?.find(
        (id: any) =>
          id.system?.toLowerCase()?.includes('uhid') ||
          id.type?.text?.toLowerCase()?.includes('uhid')
      )
      patientUhid = uhidIdentifier?.value || ''
    }

    const patientFhirId =
      t?.patientFhirId || selectedData?.fhirData?.id || selectedData?.id || ''

    const nowIso = new Date().toISOString()

    // Get the patient data that will be passed to the form
    const patientData =
      selectedPatient ||
      patient ||
      transformSelectedPatientData(selectedData) ||
      {}

    // Check if the patient has a preferred communication method
    const preferredCommunication =
      patientData?.preferredCommunication ||
      (selectedData?.whatsapp ? 'whatsapp' : '') ||
      (patientData?.whatsapp ? 'whatsapp' : '')

    return {
      employeeId: employeeId || null,
      volunteerId: volunteerId || null,
      createdOn: nowIso,
      updatedOn: nowIso,
      campId: campDetails?.id || campDetails?.campId || '',
      patientUhid: patientUhid,
      patientFhirId: patientFhirId,
      createdById: null,
      updatedById: null,
      invoiceId: 'null',
      salutation: t?.salutation
        ? t?.salutation.charAt(0).toUpperCase() +
          t?.salutation.slice(1).toLowerCase()
        : '',
      firstName: t?.firstName || '',
      middleName: t?.middleName || '',
      lastName: t?.lastName || '',
      age: t?.age || 0,
      gender: t?.gender || '',
      dob: t?.dob || '',
      mobile: t?.mobile || '',
      whatsapp:
        preferredCommunication === WhatsappText
          ? selectedData?.whatsapp || patientData?.whatsapp || ''
          : '',
      emailId: t?.emailId || '',
      town: t?.town || '',
      postalCode: t?.postalCode || '',
      photo: selectedData?.photo || '',
    }
  }

  const transformedPatient = transformSelectedPatientData(selectedPatient)

  const handleResendNotification = async (messageType: 'SMS' | 'WHATSAPP') => {
    const patientData = registrationPayload || patient
    if (!patientData?.patientUhid && !patientData?.uhid) return
    setResendLoading(true)
    try {
      // console.log(campDetails, 'Resending registration notification')
      const response = await resendNotification({
        patientUhid: patientData?.patientUhid || patientData?.uhid || '',
        patientPhoneNumber:
          patientData?.mobile ||
          patientData?.phoneNumber ||
          patientData?.phoneNo ||
          '',
        patientName: `${patientData?.firstName || ''} ${
          patientData?.lastName || ''
        }`.trim(),
        hospital: 'Medunited',
        location: campDetails?.location || '',
        notificationType: 'REGISTRATION',
        messageType: messageType,
      })
      if (response?.success) {
        toast.success(
          response?.message ?? 'Registration notification resent successfully'
        )
      } else {
        toast.error(
          response?.message ?? 'Failed to resend registration notification',
          { autoClose: false, closeOnClick: false }
        )
      }
    } finally {
      setResendLoading(false)
    }
  }

  const handlePatientSelect = (p: any) => {
    setSelectedPatient(p)
    const payload = buildRegistrationPayload(p)
    setRegistrationPayload(payload)
    setSelectSeacrh(true)
    closeSearchDialog()
  }

  const toggleSearchDialog = () => {
    setSearchDialogOpen(!searchDialogOpen)
  }

  const closeSearchDialog = () => {
    setSearchDialogOpen(false)
  }

  const patientToPass =
    registrationPayload || patient || transformedPatient || null

  useEffect(() => {
    setSelectedPatient(patient)
    setRegistrationPayload(patient)
    setSelectSeacrh(false)
  }, [patient])

  return (
    <Box
      display={'flex'}
      flexDirection='column'
      flex={1}
      width={'100%'}
      overflow={'auto'}
    >
      <Box
        mb={2}
        display={'flex'}
        justifyContent={'flex-end'}
        alignItems={'center'}
      >
        {!patient?.patientFhirId && (
          <Button
            variant='contained'
            onClick={toggleSearchDialog}
            size='small'
            className={styles.searchButton}
          >
            Search Existing Patient
          </Button>
        )}
        {(patient?.patientUhid || patient?.uhid) && (
          <Box display='flex' gap={1}>
            <Button
              variant='outlined'
              onClick={() => handleResendNotification('SMS')}
              size='small'
              disabled={resendLoading}
              className={styles.resendButton}
              startIcon={<MessageIcon fontSize='small' />}
            >
              {resendLoading ? 'Sending...' : 'Resend Notification'}
            </Button>
            <Button
              variant='outlined'
              onClick={() => handleResendNotification('WHATSAPP')}
              size='small'
              disabled={resendLoading}
              className={styles.resendButton}
              startIcon={<WhatsAppIcon fontSize='small' />}
            >
              {resendLoading ? 'Sending...' : 'Resend Notification'}
            </Button>
          </Box>
        )}
      </Box>

      <Box>
        <PatientRegistrationComponent
          camp={campDetails}
          patient={patientToPass}
          onSuccess={onSuccess}
          listPatientId={patient?.patientFhirId ?? ''}
          forceCreate={selectSeacrh}
        />
      </Box>

      <PatientSearchDialog
        open={searchDialogOpen}
        onClose={closeSearchDialog}
        onPatientSelect={handlePatientSelect}
      />
    </Box>
  )
}

export default Registration
